import { apiClient, restApiClient } from "@lobby/api-client";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";

import { useAuth } from "../../shared";

import { USE_REST_TRANSACTION_HISTORY } from "./payment.transaction-history.helpers";

import type { ApiRestTypes, SendOptions } from "@lobby/api-client";

export const Payment = {
  useDeposit() {
    return useMutation({
      mutationKey: ["Payment.deposit"],
      mutationFn: (params: SendOptions<"Payment.deposit">["params"]) =>
        apiClient.send<"Payment.deposit">({
          endpoint: "Payment.deposit",
          params,
        }),
    });
  },
  useWithdraw() {
    return useMutation({
      mutationKey: ["Payment.withdraw"],
      mutationFn: (params: SendOptions<"Payment.withdraw">["params"]) =>
        apiClient.send<"Payment.withdraw">({
          endpoint: "Payment.withdraw",
          params,
        }),
    });
  },
  useMethodList(paymentType: "deposit" | "withdraw") {
    const { isAuth } = useAuth();

    return useQuery({
      enabled: isAuth,
      queryKey: ["Payment.getMethodsList", paymentType],
      queryFn: () =>
        apiClient.send({
          endpoint: "Payment.getMethodsList",
          params: {
            paymentType,
          },
        }),
      select: (data) => data.result.data,
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });
  },
  useBankList(methodId: number, currency: string) {
    const { isAuth } = useAuth();

    return useQuery({
      enabled: isAuth,
      queryKey: ["Payment.getBankList", methodId, currency],
      queryFn: () =>
        apiClient.send({
          endpoint: "Payment.getBankList",
          params: {
            methodId,
            currency,
          },
        }),
    });
  },
  useCurrencyList(methodId: number, currency: string) {
    const { isAuth } = useAuth();

    return useQuery({
      enabled: isAuth,
      queryKey: ["Payment.getCurrencyList", methodId, currency],
      queryFn: () =>
        apiClient.send({
          endpoint: "Payment.getCurrencyList",
          params: {
            methodId,
            currency,
          },
        }),
    });
  },
  useActiveWithdrawRequest() {
    return useQuery({
      staleTime: 30_000,
      queryKey: ["Payment.filter"],
      queryFn: () =>
        apiClient.send({
          endpoint: "Payment.filter",
          params: {
            type: "withdraw",
            status: "active",
            limit: 1,
            offset: 0,
          },
        }),
    });
  },
  useTransactionHistory(paymentType: "deposit" | "withdraw") {
    const { isAuth } = useAuth();

    return useInfiniteQuery({
      enabled: isAuth && !USE_REST_TRANSACTION_HISTORY,
      queryKey: ["Payment.filter", paymentType],
      queryFn: ({ pageParam }) =>
        apiClient.send({
          endpoint: "Payment.filter",
          params: {
            type: paymentType,
            limit: 30,
            offset: pageParam * 30,
          },
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        if (lastPage.result?.data.length === 0) {
          return undefined;
        }
        return lastPageParam + 1;
      },
      getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
        if (firstPageParam <= 1) {
          return undefined;
        }
        return firstPageParam - 1;
      },
    });
  },
  useBuildSwap() {
    return useMutation({
      mutationKey: ["Payment.buildSwap"],
      mutationFn: (params: SendOptions<"Payment.buildSwap">["params"]) =>
        apiClient.send<"Payment.buildSwap">({
          endpoint: "Payment.buildSwap",
          params,
        }),
    });
  },
  useMinDepositAmount(methodId: number, currency: string, payCurrency: string) {
    return useQuery({
      enabled: Boolean(currency && payCurrency),
      queryKey: ["Payment.getMinAmount", methodId, currency, payCurrency],
      queryFn: () =>
        apiClient.send({
          endpoint: "Payment.getMinAmount",
          params: {
            methodId,
            currency,
            payCurrency,
          },
        }),
    });
  },
  useWithdrawAvailableAmount(
    query: Required<
      ApiRestTypes.Input<"GET /api/v1/player/balance/withdraw/available-amount">["query"]
    >,
  ) {
    return useQuery({
      enabled: false,
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: ["Payment.withdraw.availableAmount"],
      queryFn: () =>
        restApiClient.send({
          endpoint: "GET /api/v1/player/balance/withdraw/available-amount",
          query,
        }),
    });
  },
};
